/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, LargeCloseIcon, Pressable, Icon, Text } from '@components/atoms'
import { motion } from 'framer-motion'

import { grey } from '@mui/material/colors'

interface PropsType {
	showSubContent: boolean
	leftSideContent?: JSX.Element
	centerMainContent?: JSX.Element
	rightSubContent?: JSX.Element
	onCloseClick?: () => void
	onCloseRightSubContent?: () => void
}

export const FullModalMotionLayout = (props: PropsType) => {
	const {
		showSubContent,
		onCloseRightSubContent,
		centerMainContent,
		leftSideContent,
		rightSubContent,
	} = props

	const showView: string = showSubContent === true ? 'detail' : 'master'

	const onCloseClick = React.useCallback(() => {
		if (props.onCloseClick) {
			props.onCloseClick()
		}
	}, [])

	const onRightArrowClick = () => {
		if (onCloseRightSubContent) {
			onCloseRightSubContent()
		}
	}

	return (
		<View css={style}>
			<View className="fullModalView">
				{showSubContent ? (
					<Pressable className="closeButton" onClick={onRightArrowClick}>
						<Icon name="arrow-right" className="rightArrowIcon" />
					</Pressable>
				) : (
					<Pressable className="closeButton" onClick={onCloseClick}>
						<LargeCloseIcon strokeColor={grey[100]} />
					</Pressable>
				)}

				<View className="contentView">
					<motion.div
						className="leftMarginView"
						initial={false}
						animate={
							!showSubContent ? { opacity: 1, width: '17rem' } : { opacity: 0, width: 0 }
						}
						transition={{ duration: 0.3 }}
					>
						{leftSideContent}
					</motion.div>

					<motion.div
						className="contentInnerView"
						initial={false}
						animate={
							!showSubContent
								? {
										width: '60rem',
										left: 0,
								  }
								: {
										width: '100%',
										left: 0,
								  }
						}
					>
						<motion.div
							className="mainContentView"
							initial={false}
							animate={
								!showSubContent
									? {
											width: '100%',
											minWidth: '50rem',
											paddingLeft: '8rem',
											paddingRight: '8rem',
									  }
									: {
											width: '50%',
											minWidth: 0,
											paddingLeft: '4rem',
											paddingRight: '4rem',
									  }
							}
							transition={{ duration: 0.3 }}
						>
							{centerMainContent}
						</motion.div>

						<motion.div
							className="rightSubContentView"
							initial={false}
							animate={
								showSubContent
									? {
											opacity: 1,
											width: '50%',
											paddingRight: '4rem',
											paddingLeft: '4rem',
									  }
									: {
											opacity: 0,
											width: 0,
											paddingRight: 0,
											paddingLeft: 0,
									  }
							}
							transition={{ duration: 0.3 }}
						>
							{rightSubContent}
						</motion.div>
					</motion.div>
				</View>
			</View>

			<View className={`mobileFullModalView ${showView}`}>
				<View className="mobileFullModalTopBar">
					<Pressable
						className="mobileLeftButton"
						onClick={showView === 'master' ? onCloseClick : onRightArrowClick}
					>
						<Icon name="chevron-left" className="icon" />
					</Pressable>
				</View>

				<View className="mobileContentView">
					<View className="mobileContentInnerView">
						<View className="mobileMainContentView">{centerMainContent}</View>
						<View className="mobileRightSubContentView">{rightSubContent}</View>
					</View>
				</View>
			</View>
		</View>
	)
}

const style = css({
	'.fullModalView': {
		display: 'block',
		position: 'fixed',
		width: '100vw',
		height: '100vh',
		overflow: 'hidden',

		'.closeButton': {
			position: 'absolute',
			zIndex: 10,
			top: '1rem',
			right: '1rem',

			'.rightArrowIcon': {
				fontSize: '60px',
				WebkitTextStroke: `3px ${grey[200]}`,
			},
		},

		'.contentView': {
			display: 'flex',
			justifyContent: 'center',
			height: '100%',

			'.leftMarginView': {
				position: 'absolute',
				height: '100vh',
				width: '17rem',
				top: 0,
				left: 0,
				background: grey[200],
			},

			'.contentInnerView': {
				position: 'relative',
				height: 'fit-contents',
				// overflow: 'auto',

				'.mainContentView': {
					boxSizing: 'border-box',
					position: 'absolute',
					left: 0,
				},

				'.rightSubContentView': {
					boxSizing: 'border-box',
					position: 'absolute',
					height: '100%',
					right: 0,
					background: grey[200],
					overflow: 'hidden',
				},
			},
		},
	},

	'.mobileFullModalView': {
		display: 'none',
	},

	'@media (max-width: 900px)': {
		'.fullModalView': {
			display: 'none',
		},

		'.mobileFullModalView': {
			display: 'block',
			position: 'fixed',
			width: '100%',
			height: '100%',
			overflow: 'hidden',

			'.mobileFullModalTopBar': {
				position: 'absolute',
				zIndex: 10,
				width: '100%',
				height: '3rem',
				background: '#fff',

				'.mobileLeftButton': {
					position: 'absolute',
					zIndex: 10,
					width: '3rem',
					height: '3rem',
					top: 0,
					left: 0,
				},
			},

			'&.master': {
				'.mobileContentView': {
					display: 'flex',
					justifyContent: 'center',
					width: '100%',
					height: '100%',

					'.mobileLeftMarginView': {
						display: 'none',
					},

					'.mobileContentInnerView': {
						position: 'relative',
						height: '100%',
						// overflow: 'auto',
						width: '100%',
						left: 0,

						'.mobileMainContentView': {
							boxSizing: 'border-box',
							position: 'absolute',
							width: '100%',
							height: '100%',
							paddingLeft: '1rem',
							paddingRight: '1rem',
						},

						'.mobileRightSubContentView': {
							display: 'none',
						},
					},
				},
			},

			'&.detail': {
				'.mobileContentView': {
					display: 'flex',
					justifyContent: 'center',
					width: '100%',
					height: '100%',

					'.mobileLeftMarginView': {
						display: 'none',
					},

					'.mobileContentInnerView': {
						position: 'relative',
						height: '100%',
						// overflow: 'auto',
						width: '100%',
						left: 0,

						'.mobileMainContentView': {
							display: 'none',
						},

						'.mobileRightSubContentView': {
							boxSizing: 'border-box',
							position: 'absolute',
							width: '100%',
							height: '100%',
							paddingLeft: '1rem',
							paddingRight: '1rem',
						},
					},
				},
			},
		},
	},
})
